import { getConfig } from '@shared/lib/config';
import { Grid, Layout, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { ReactNode } from 'react';

const { Title, Text } = Typography;

type AuthShellProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
};

/**
 * Каркас экранов авторизации (login / verify-otp).
 *
 * На десктопе (≥lg) — split-screen: слева тёмная колонка с брендом, справа
 * карточка формы (характерно для fintech-админок — Mercury, Brex).
 * На мобильном брендовая колонка скрыта, вместо неё компактная строка
 * с логотипом над формой.
 */
export function AuthShell({ title, subtitle, children, footer }: AuthShellProps) {
  const { t } = useTranslation('auth');
  const cfg = getConfig();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.lg === false;

  const logo = (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 6,
        background: isMobile ? '#0F172A' : '#F8FAFC',
        color: isMobile ? '#F8FAFC' : '#0F172A',
        display: 'grid',
        placeItems: 'center',
        fontWeight: 700,
        fontSize: 16,
        letterSpacing: -0.5,
      }}
    >
      P
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: '#FFFFFF' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 480px) 1fr',
          minHeight: '100vh',
        }}
      >
        {!isMobile && (
          <aside
            style={{
              background: '#0F172A',
              color: '#F8FAFC',
              padding: '48px 56px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {logo}
              <Text style={{ color: '#F8FAFC', fontWeight: 600, fontSize: 15 }}>{cfg.appName}</Text>
            </div>

            <div>
              <Title
                level={2}
                style={{
                  color: '#F8FAFC',
                  fontWeight: 600,
                  marginBottom: 16,
                  letterSpacing: -0.5,
                  lineHeight: 1.2,
                }}
              >
                {t('brand.headline1')}
                <br />
                {t('brand.headline2')}
              </Title>
              <Text style={{ color: '#94A3B8', fontSize: 14, lineHeight: 1.6 }}>
                {t('brand.tagline')}
              </Text>
            </div>

            <Text style={{ color: '#64748B', fontSize: 12 }}>
              © {new Date().getFullYear()} JSoft · {cfg.env.toUpperCase()}
            </Text>
          </aside>
        )}

        <main
          style={{
            display: 'grid',
            placeItems: 'center',
            padding: isMobile ? '32px 16px' : '48px 24px',
            background: '#F8FAFC',
          }}
        >
          <div style={{ width: '100%', maxWidth: 400 }}>
            {isMobile && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 32,
                }}
              >
                {logo}
                <Text style={{ fontWeight: 600, fontSize: 15 }}>{cfg.appName}</Text>
              </div>
            )}
            <Title level={3} style={{ marginBottom: 6, fontWeight: 600, letterSpacing: -0.3 }}>
              {title}
            </Title>
            {subtitle && (
              <Text type="secondary" style={{ display: 'block', marginBottom: 32, fontSize: 14 }}>
                {subtitle}
              </Text>
            )}
            <div>{children}</div>
            {footer && <div style={{ marginTop: 24 }}>{footer}</div>}
          </div>
        </main>
      </div>
    </Layout>
  );
}
